import React, {
  useState,
  useRef,
  useEffect,
  useMemo,
  useCallback,
} from 'react';
import { MotifTable, MotifButton } from '@ey-xd/motif-react';
import CustomHeaderTemplate from '../shared/customHeaders/CustomHeaderTemplate.js';
import CustomPagination from '../../common/MotifPagination';
import PropTypes from 'prop-types';
import OwnershipPercentageCellEditor from './customCellEditor/OwnershipPercentageCellEditor.js';
import {
  onCellEditingStarted,
  onCellEditingStopped,
  onCellValueChanged,
} from '../../common/AgGridEditableUtils';

const OWNERSHIP_DISPLAY_MAX_DECIMAL_PLACES = 10;

const OwnershipTable = ({
  ownershipData,
  gridColumnState,
  handleGridChanges,
  handleGridColumnStateChange,
  isEditModeActive,
  toggleGridEditMode,
  gridDataAfterAction,
  resetUpdatedRowDataTrigger,
}) => {
  const gridRef = useRef();
  const [rowData, setRowData] = useState([]);
  const [updatedRowData, setUpdatedRowData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const rowDataRef = useRef({});
  rowDataRef.current = rowData;

  // Instruction text for the overlay
  const hasData = ownershipData && ownershipData.length > 0; //Default column settings
  const defaultColDef = useMemo(() => {
    return {
      flex: 1,
      minWidth: 65,
      filter: true,
      resizable: true,
      sortable: true,
      wrapText: true,
      autoHeight: true,
    };
  }, []);

  // List of field names that should trigger the `onCellEditingStarted` event.
  const fieldsToCheckForCellEditingStartedEvent = ['percentageOfOwnership'];

  // List of field names that should trigger the `onCellEditingStopped` event.
  const fieldsToCheckForCellEditingStoppedEvent = ['percentageOfOwnership'];

  // List of field names that should trigger the `onCellValueChanged` event.
  // The `onCellValueChanged` event is not triggered for Motif components, so this list is not used in the current implementation.
  const fieldsToCheckForCellValueChangedEvent = [];

  // Add a new state variable to track the Save button state
  const [isSaveEnabled, setIsSaveEnabled] = useState(false);
  const [lastFocusedCell, setLastFocusedCell] = useState(null);

  // Function to determine if a cell has been modified compared to its original value.
  // If the original value differs from the current cell value, it returns true, triggering a visual indicator.
  const cellUpdatedIndicator = (params) => {
    // Proceed with regular checks for updated values
    if (isEditModeActive && updatedRowData && updatedRowData.length > 0) {
      let matchedRow = updatedRowData.find(
        (row) => row.uniqueKey === params.data.uniqueKey
      );
      let columnName = 'original_' + params.colDef.field;
      if (
        matchedRow &&
        Object.prototype.hasOwnProperty.call(matchedRow, columnName) &&
        matchedRow[columnName] !== params.data[params.colDef.field]
      ) {
        setIsSaveEnabled(true); // Enable the Save button when percentageOfOwnership is edited
        return true;
      }
    }
    return false;
  };

  const [cellErrors, setCellErrors] = useState({});

  const handleCellValidationError = useCallback((error, uniqueKey) => {
    setCellErrors((prev) => ({ ...prev, [uniqueKey]: error }));
  }, []);
  const columnDefs = useMemo(
    () => [
      {
        field: 'reitTrackingID',
        headerName: 'REITSuite Entity ID for REIT',
        sortable: true,
        showRowGroup: false,
        rowGroup: false,
        minWidth: 100,
        valueGetter: (params) => {
          return params.data && params.data.reitTrackingID;
        },
        headerComponentParams: {
          template: CustomHeaderTemplate('REITSuiteEntityIDforREIT'),
        },
      },
      {
        field: 'reitName',
        headerName: 'REIT Name',
        sortable: true,
        showRowGroup: false,
        rowGroup: false,
        minWidth: 100,
        valueGetter: (params) => {
          return params.data && params.data.reitName;
        },
        headerComponentParams: { template: CustomHeaderTemplate('REITName') },
      },
      {
        field: 'propertyName',
        headerName: 'Property Name',
        sortable: true,
        showRowGroup: false,
        hide: false,
        rowGroup: false,
        minWidth: 100,
        valueGetter: (params) => {
          return params.data && params.data.propertyName;
        },
        headerComponentParams: {
          template: CustomHeaderTemplate('PropertyName'),
        },
        spanHeaderHeight: true,
      },
      {
        field: 'propertyTrackingID',
        headerName: 'REITSuite Entity ID for Property',
        sortable: true,
        showRowGroup: false,
        hide: false,
        rowGroup: false,
        minWidth: 100,
        valueGetter: (params) => {
          return params.data && params.data.propertyTrackingID;
        },
        headerComponentParams: {
          template: CustomHeaderTemplate('REITSuiteEntityIdForProperty'),
        },
        spanHeaderHeight: true,
      },
      {
        field: 'propertyTypeDescription',
        headerName: 'Property Type',
        sortable: true,
        showRowGroup: false,
        hide: false,
        rowGroup: false,
        minWidth: 100,
        valueGetter: (params) => {
          return params.data && params.data.propertyTypeDescription;
        },
        headerComponentParams: {
          template: CustomHeaderTemplate('PropertyType'),
        },
        spanHeaderHeight: true,
      },
      {
        field: 'percentageOfOwnership',
        headerName: 'Ownership %',
        cellClass: 'ownership-cell-editor',
        sortable: true,
        showRowGroup: false,
        hide: false,
        rowGroup: false,
        minWidth: 100,
        valueGetter: (params) => {
          const percentage = params.data && params.data.percentageOfOwnership;
          if (
            percentage === null ||
            percentage === undefined ||
            percentage === ''
          ) {
            return '';
          }

          const percentageValue = Number(percentage);

          if (Number.isNaN(percentageValue)) {
            return '';
          }

          return ((percentageValue * 1000.0) / 100000.0).toLocaleString(
            undefined,
            {
                style: 'percent',
                minimumFractionDigits: 4,
                maximumFractionDigits: OWNERSHIP_DISPLAY_MAX_DECIMAL_PLACES,
              }
          );
        },
        headerComponentParams: {
          template: CustomHeaderTemplate('Ownership%'),
        },
        spanHeaderHeight: true,
        resizable: true,
        editable: (params) =>
          isEditModeActive &&
          params.data.propertyTrackingID &&
          params.data.propertyName
            ? true
            : false,
        cellEditorSelector: (params) => {
          if (params.data.propertyTrackingID && params.data.propertyName) {
            return {
              component: OwnershipPercentageCellEditor,
              params: {
                handleOwnershipPercentageChange: (
                  ownershipPercentage,
                  uniqueKey,
                  columnName
                ) => {
                  // Update other service name in updatedRowData
                  const mergedArray =
                    syncRowDataToUpdatedRowData(gridDataAfterAction);
                  const modifiedData = mergedArray.map((obj) => {
                    if (obj.uniqueKey === uniqueKey) {
                      return {
                        ...obj,
                        [columnName]: ownershipPercentage,
                      };
                    }
                    return obj;
                  });
                  setUpdatedRowData(modifiedData);
                },
                onValidationError: handleCellValidationError,
              },
            };
          } else {
            return null;
          }
        },
        cellClassRules: {
          'editable-cell-indicator': (params) =>
            isEditModeActive &&
            params.data.propertyTrackingID &&
            params.data.propertyName &&
            params.data.percentageOfOwnership !== null &&
            params.data.percentageOfOwnership !== undefined &&
            params.data.percentageOfOwnership !== ''
              ? true
              : false,
          'cell-updated-indicator': cellUpdatedIndicator,
        },
      },
    ],
    [isEditModeActive, updatedRowData, gridDataAfterAction]
  );

  const [itemsPerPage, setItemsPerPage] = useState(100);

  //Grid Global Listner
  const gridGlobalListner = useCallback(function (type, event) {
    if (type.indexOf('columnVisible') >= 0) {
      handleGridColumnsChanged(event);
    }

    // Bind handleFilterChanged event to filterChanged and rowDataUpdated events
    if (type.indexOf('filterChanged') >= 0) {
      setTimeout(function () {
        handleFilterChanged(event);
      }, 0);
    }

    if (type.indexOf('rowDataUpdated') >= 0) {
      setTimeout(function () {
        handleFilterChanged(event);
      }, 0);
    }
  }, []);

  //Handle Grid Ready event
  const handleGridReady = useCallback(
    (event) => {
      //Remove event for column state change
      event.api.removeEventListener(gridGlobalListner);

      //Add event for column state change
      event.api.addGlobalListener(gridGlobalListner);

      //Apply column state
      var columnState = localStorage.getItem('REITSuiteOwnershipColumnState');
      if (columnState) {
        gridRef.current.columnApi.applyColumnState({
          state: JSON.parse(columnState),
          applyOrder: true,
        });
      }

      // Set the page size and total pages on the first load
      handleItemsPerPageChange(100); // Setting it 100 to make sure the default number of items on load is always set to 100
    },
    [gridGlobalListner]
  );

  // Handle OnFilterChanged event
  const handleFilterChanged = useCallback(
    (event) => {
      if (gridRef && gridRef.current && gridRef.current.api) {
        let calcTotalPages = gridRef.current.api.paginationGetTotalPages();
        setTotalPages(calcTotalPages);

        if (currentPage > calcTotalPages) {
          setCurrentPage(
            calcTotalPages === 0 ? calcTotalPages : calcTotalPages
          );
        }

        const gridCurrentPage =
          gridRef.current.api.paginationGetCurrentPage() + 1; // Adding 1 since it returns zero-based index
        if (currentPage < gridCurrentPage && calcTotalPages !== 0) {
          setCurrentPage(gridCurrentPage);
        }

        if (currentPage <= 1 && gridCurrentPage === 1 && calcTotalPages !== 0) {
          setCurrentPage(gridCurrentPage);
        }
      }
    },
    [currentPage]
  );

  //Handle First Data Renderered event
  const handleFirstDataRendered = useCallback((event) => {
    gridRef.current.columnApi.autoSizeAllColumns();
  }, []);

  //Handle Grid Columns changed event
  const handleGridColumnsChanged = useCallback(
    (event) => {
      var currentColumnState = gridRef.current.columnApi.getColumnState();
      var localStorageColumnState = localStorage.getItem(
        'REITSuiteOwnershipColumnState'
      );

      // If the column state details are not exists in local storage then save the changed Column state data
      if (
        currentColumnState &&
        currentColumnState.filter((col) => col.hide == true)?.length > 0 &&
        !localStorageColumnState
      ) {
        saveUserPreferenceDetails();
      } else if (localStorageColumnState) {
        // If the column state details are exists in local storage then save the changed Column state data only if difference is found
        var columnsStateArray = JSON.parse(localStorageColumnState);
        if (
          columnsStateArray &&
          columnsStateArray.length > 0 &&
          currentColumnState &&
          currentColumnState.length > 0 &&
          columnsStateArray.filter((col) => col.hide == true)?.length !=
            currentColumnState.filter((col) => col.hide == true)?.length
        ) {
          //If column state mismatch found then only save column state data to avoid saving same state
          saveUserPreferenceDetails();
        }
      }

      //Trigger Grid Column state change (of parent) to sync column state data
      handleGridColumnStateChange(currentColumnState);

      gridRef &&
        gridRef.current &&
        gridRef.current.columnApi &&
        gridRef.current.columnApi.autoSizeAllColumns();
    },
    [handleGridColumnStateChange]
  );

  //Save User Preference details (Column state)
  const saveUserPreferenceDetails = useCallback(() => {
    var columnState = gridRef.current.columnApi.getColumnState();
    if (columnState) {
      localStorage.setItem(
        'REITSuiteOwnershipColumnState',
        JSON.stringify(columnState)
      );
    }
  }, []);

  //Handle Grid Items per Page change event
  const handleItemsPerPageChange = useCallback((val) => {
    gridRef.current.api.paginationGoToFirstPage();
    gridRef.current.api.setGridOption('paginationPageSize', Number(val));
    setCurrentPage(1);
    setItemsPerPage(val);
    // Get total pages from Grid's default pagination control and apply it to custom pagination control
    if (gridRef && gridRef.current && gridRef.current.api) {
      let calcTotalPages = gridRef.current.api.paginationGetTotalPages();
      setTotalPages(calcTotalPages);
      //If total page is zero then set the current page as zero
      if (calcTotalPages == 0) {
        setCurrentPage(calcTotalPages);
      }
    }
  }, []);

  //Handle Grid Page change event
  const handlePageChange = useCallback((val) => {
    setCurrentPage(val);
    gridRef.current.api.paginationGoToPage(val - 1);
  }, []);

  /**
   * Synchronizes 'rowData' to 'updatedRowData', maintaining order and integrity.
   *
   * - Uses `sourceData` if provided as input parameter; otherwise, defaults to `rowData`.
   * - Creates a copy of `updatedRowData` if available, or initializes an empty array.
   * - Iterates through `updatedRowData` to find matching entries in the reference dataset using `uniqueKey`.
   * - Merges matching objects, copying all fields except those that start with "original".
   * - Ensures missing rows from `rowData` or `sourceData` are inserted at their correct positions.
   * - Returns the updated array with synchronized data while preserving row order.
   *
   * If no match is found, the existing item from `updatedRowData` remains unchanged.
   *
   * @param {Array} [sourceData=[]] - Optional dataset used for synchronization; defaults to `rowData` if empty.
   * @returns {Array} Updated row data array with merged values and correctly positioned missing rows.
   */
  useEffect(() => {
    // Reset all the tracked changes of row data
    setUpdatedRowData([]);
  }, [resetUpdatedRowDataTrigger]);

  // useEffect(() => {
  //     setRowData(ownershipData);
  // }, [ownershipData]);
  useEffect(() => {
    if (rowData.length > 0 && gridRef?.current?.api !== undefined) {
      handleGridChanges(rowData, gridRef.current.api);
      let calcTotalPages = gridRef.current.api.paginationGetTotalPages();
      setTotalPages(calcTotalPages);
      // If total page is zero then set the current page as zero
      if (calcTotalPages === 0) {
        setCurrentPage(calcTotalPages);
      }
    }
  }, [rowData]);

  useEffect(() => {
    if (
      isEditModeActive &&
      lastFocusedCell &&
      gridRef.current &&
      gridRef.current.api
    ) {
      gridRef.current.api.ensureIndexVisible(
        lastFocusedCell.rowIndex,
        'middle'
      );
      gridRef.current.api.setFocusedCell(
        lastFocusedCell.rowIndex,
        lastFocusedCell.colId
      );
      gridRef.current.api.startEditingCell({
        rowIndex: lastFocusedCell.rowIndex,
        colKey: lastFocusedCell.colId,
      });
    }
  }, [rowData, isEditModeActive]);

  useEffect(() => {
    setRowData(ownershipData);
    if (gridRef && gridRef.current && gridRef.current.api) {
      gridRef.current.api.redrawRows();
      gridRef.current.api.refreshHeader();
      // Get total pages from Grid's default pagination control and apply it to custom pagination control
      let calcTotalPages = gridRef.current.api.paginationGetTotalPages();
      setTotalPages(calcTotalPages);
    }
  }, [ownershipData]);

  useEffect(() => {
    setTimeout(function () {
      if (
        Array.isArray(gridDataAfterAction) &&
        gridDataAfterAction.length > 0
      ) {
        setRowData(gridDataAfterAction);
        const mergedArray = syncRowDataToUpdatedRowData(gridDataAfterAction);
        setUpdatedRowData(mergedArray);
      }
    }, 0);
  }, [gridDataAfterAction]);

  useEffect(() => {
    //Reset Grid to default column state
    //When Grid Column state changes due to Reset Column State action then Reset Grid to default column state
    var localStorageColumnState = localStorage.getItem(
      'REITSuiteOwnershipColumnState'
    );
    if (
      !localStorageColumnState &&
      gridRef &&
      gridRef.current &&
      gridRef.current.api
    ) {
      gridRef.current.api.setColumnDefs([]);
      gridRef.current.api.setColumnDefs(columnDefs);
    }
  }, [gridColumnState]);

  const rowHeight = 76;
  const headerHeight = 45;
  const totalHeight = 5 * rowHeight + headerHeight; // set 5 rows as default

  /**
   * Synchronizes 'rowData' to 'updatedRowData', maintaining order and integrity.
   *
   * - Uses `sourceData` if provided as input parameter; otherwise, defaults to `rowData`.
   * - Creates a copy of `updatedRowData` if available, or initializes an empty array.
   * - Iterates through `updatedRowData` to find matching entries in the reference dataset using `uniqueKey`.
   * - Merges matching objects, copying all fields except those that start with "original".
   * - Ensures missing rows from `rowData` or `sourceData` are inserted at their correct positions.
   * - Returns the updated array with synchronized data while preserving row order.
   *
   * If no match is found, the existing item from `updatedRowData` remains unchanged.
   *
   * @param {Array} [sourceData=[]] - Optional dataset used for synchronization; defaults to `rowData` if empty.
   * @returns {Array} Updated row data array with merged values and correctly positioned missing rows.
   */
  const syncRowDataToUpdatedRowData = useCallback(
    (sourceData = []) => {
      // Determine the source data to use
      const referenceData =
        Array.isArray(sourceData) && sourceData.length > 0
          ? sourceData
          : rowData;

      // Convert updatedRowData into a Map for quick lookups
      const updatedRowMap = new Map(
        Array.isArray(updatedRowData)
          ? updatedRowData.map((row) => [row?.uniqueKey, row])
          : []
      );

      // Process referenceData to ensure all missing rows are inserted at the correct positions
      const mergedArray = referenceData.map((firstItem) => {
        if (updatedRowMap.has(firstItem.uniqueKey)) {
          const secondItem = updatedRowMap.get(firstItem.uniqueKey);

          // Copy fields while excluding "original" fields
          const filteredFields = Object.keys(firstItem).reduce((acc, key) => {
            if (!key.startsWith('original')) {
              acc[key] = firstItem[key];
            }
            return acc;
          }, {});

          return { ...secondItem, ...filteredFields };
        }

        return firstItem; // If not found in updatedRowData, keep it as-is
      });

      return mergedArray; // Preserves referenceData order while merging updates from updatedRowData
    },
    [rowData, updatedRowData]
  );

  return (
    <React.Fragment>
      <div className="row mb-10">
        <div className="col mt-3">
          <div
            data-testid="grdOwnershipDataGrid"
            className="ag-theme-quartz motif-table table-bd-custom-height-width-ownership"
          >
            <MotifTable
              zebra={true}
              compact={true}
              rowData={rowData}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              accentedSort={true}
              rowSelection="multiple"
              suppressRowClickSelection={true}
              onGridReady={handleGridReady}
              onFirstDataRendered={handleFirstDataRendered}
              ref={gridRef}
              pagination={true}
              suppressPaginationPanel={true}
              paginationPageSize={100}
              groupDisplayType={'custom'}
              groupSelectsChildren={true}
              groupDefaultExpanded={0}
              onFilterChanged={handleFilterChanged}
              data-testid="grdOwnership%DataGrid"
              onCellEditingStarted={(event) =>
                onCellEditingStarted(
                  event,
                  fieldsToCheckForCellEditingStartedEvent,
                  updatedRowData,
                  rowData,
                  setUpdatedRowData
                )
              }
              onCellEditingStopped={(event) =>
                onCellEditingStopped(
                  event,
                  fieldsToCheckForCellEditingStoppedEvent,
                  updatedRowData,
                  rowData,
                  setUpdatedRowData,
                  setRowData
                )
              }
              onCellValueChanged={(event) =>
                onCellValueChanged(
                  event,
                  fieldsToCheckForCellValueChangedEvent,
                  updatedRowData,
                  rowData,
                  setUpdatedRowData,
                  setRowData
                )
              }
              singleClickEdit={true}
              rowHeight={70}
              onCellFocused={(event) => {
                if (
                  event &&
                  event.rowIndex !== null &&
                  event.rowIndex !== undefined &&
                  event.column
                ) {
                  setLastFocusedCell({
                    rowIndex: event.rowIndex,
                    colId: event.column.getColId(),
                  });
                }
              }}
            />
            {rowData.length > 0 && (
              <div className="mt-3">
                {isEditModeActive && (
                  <React.Fragment>
                    <span className="right mb-3">
                      <MotifButton
                        variant="primary"
                        type="button"
                        data-testid="btnSave"
                        onClick={() => {
                          gridRef?.current?.api?.clearFocusedCell();
                          setLastFocusedCell(null);
                          setTimeout(() => {
                            toggleGridEditMode('save', gridRef?.current?.api);
                            // --- Add this block to refresh state after save ---
                            if (gridDataAfterAction) {
                              setRowData(gridDataAfterAction);
                              const mergedArray =
                                syncRowDataToUpdatedRowData(
                                  gridDataAfterAction
                                );
                              setUpdatedRowData(mergedArray);
                            }
                            setIsSaveEnabled(false); // Reset Save button state
                          }, 1000); // Ensures this executes after the clearFocusedCell() UI change
                        }}
                        style={{ maxHeight: '3rem' }}
                        disabled={
                          !isSaveEnabled ||
                          Object.values(cellErrors).some((e) => !!e)
                        } // Disable the button if no edits have been made
                      >
                        Save
                      </MotifButton>
                    </span>
                    <span className="right mb-3 mr-3 ml-5">
                      <MotifButton
                        variant="secondary"
                        type="button"
                        data-testid="btnCancel"
                        onClick={() => {
                          gridRef?.current?.api?.clearFocusedCell();
                          setLastFocusedCell(null);
                          setTimeout(() => {
                            setUpdatedRowData([]);
                            toggleGridEditMode('cancel', gridRef?.current?.api);
                          }, 100); // Ensures this executes after the clearFocusedCell() UI change
                        }}
                        style={{ maxHeight: '3rem' }}
                      >
                        Cancel
                      </MotifButton>
                    </span>
                  </React.Fragment>
                )}
                <span className="right mb-3">
                  <CustomPagination
                    currentPage={currentPage}
                    handlePageChange={handlePageChange}
                    totalPages={totalPages}
                    handleItemsPerPageChange={handleItemsPerPageChange}
                    gridRef={gridRef}
                    setCurrentPage={setCurrentPage}
                    setItemsPerPage={setItemsPerPage}
                    setTotalPages={setTotalPages}
                  />
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    </React.Fragment>
  );
};

OwnershipTable.propTypes = {
  ownershipData: PropTypes.array.isRequired,
  gridColumnState: PropTypes.array.isRequired,
  handleGridChanges: PropTypes.func,
  toggleGridEditMode: PropTypes.func.isRequired,
  gridDataAfterAction: PropTypes.object.isRequired,
  resetUpdatedRowDataTrigger: PropTypes.number.isRequired,
};

export default OwnershipTable;
